import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { getRequestParams, request } from 'services/server';
import { ErrorType } from 'services/server';
import {CreativeType} from 'types/creative';
import useResizeObserver from '@react-hook/resize-observer'

type ResponseType = {
  data?: any, // TODO: update to handle multiple reponse types or create unique types per call (see the state types in useFetch/usePost)
  status?: number
};

export type InitDataType = {
  name: 'string',
}

export type UseRequestType = {
  result: ResponseType, // TODO: should elaborate
  loading: boolean,
  error: ErrorType | null,
  resetError?: () => void,
};

// Example multiple API calls at once if needed
// export const useRequests = (urls: string[]) => {
//   const [data, setData] = useState<UseRequestType>({
//     result: {},
//     loading: false,
//     error: null,
//     results: [],
//   });

//   const resetError = () => setData(prevData => ({
//     ...prevData,
//     error: null,
//   }));
//   // if need for dynamic methods, urls paramaeter could be an array with objects instead to pass as request options when fetch invoked
//   const urlsToFetch: Promise<Response>[] = urls.map(url => fetch(url));
//   useEffect(() => {
//     setData(prevData => ({
//       ...prevData,
//       loading: true,
//     }));
//     Promise.all(urlsToFetch)
//       .then((responses) =>
//         Promise.all(responses.map((response) => response.json()))
//       ).then((data) => {
//         const mapResults = data.map(obj => {
//           console.log(obj)
//           return obj.data ? { error: null, success: obj } : { success: null, error: obj, }
//         });
//         setData(prevData => ({
//           ...prevData,
//           loading: false,
//           results: mapResults,
//         }));
//     }).catch((error) => {
//       setData(prevData => ({
//         ...prevData,
//         loading: false,
//         error: error.response || error,
//       }));
//     });
//   }, []);
//   return {...data, resetError };
// }

export const useRequest = (url: string, method: string = 'GET', payload: any = null) => {
  const [data, setData] = useState<UseRequestType>({
    result: {},
    loading: false,
    error: null,
  });

  const resetError = () => setData(prevData => ({
    ...prevData,
    error: null,
  }));
  
  useEffect(() => {
    (async () => {
      if (url) {
        try {
          setData(prevData => ({
            ...prevData,
            loading: true,
            error: null
          }));
          
          const requestOptions = getRequestParams(method, payload);
          const { success: response, error } = await request(url, requestOptions);
          
          if (response) {
            setData(prevData => ({
              ...prevData,
              loading: false,
              result: response,
            }));
          } else {
            // Handle error properly
            let errorMessage = 'An unknown error occurred';
            let errorStatus = 500;
            
            if (error) {
              const { errors, status, message } = error;
              errorStatus = status || 500;
              errorMessage = message || (errors && errors.length > 0 ? errors[0].message : 'Request failed');
            }
            
            setData(prevData => ({
              ...prevData,
              loading: false,
              error: { status: errorStatus, message: errorMessage },
            }));
          }
        } catch (err) {
          console.error('Request failed with error:', err);
          setData(prevData => ({
            ...prevData,
            loading: false,
            error: { 
              status: 500, 
              message: err instanceof Error ? err.message : 'Unknown error'
            },
          }));
        }
      }
    })();
  }, [url]);
  return {...data, resetError };
};

export const useLocalStorage = (key: string) => {
  const [val, setVal] = useState<string>(localStorage.getItem(key) || '');
  
  useEffect(() => localStorage.setItem(key, val), [key, val]);
  
  return [val, setVal] as const;
}

export const useViewport = () => {
  const [width, setWidth] = useState<number>(window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth);
  const [height, setHeight] = useState<number>(window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight);
  useEffect(() => {
    const handleWindowResize = () => {
      setWidth(window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth);
      setHeight(window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight);
    }
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);
  return { width, height };
}

export const usePrevious = <T>(val: T): T | undefined => {
  // ref hook is a generic object container
  const reference = useRef<T>();

  // store current value in ref
  useEffect(() => {
    reference.current = val;
  }, [val]); // only rerun if value changes

  return reference.current;
};


export const useDivSizeChange = (target: React.RefObject<HTMLElement>) => {
  const [size, setSize] = useState({width:0, height:0})
  useLayoutEffect(() => {
    target.current && setSize({width: target.current.getBoundingClientRect().width, height: target.current.getBoundingClientRect().height})
  }, [target])

  // Where the magic happens
  useResizeObserver(target.current, (entry) => setSize({width: entry.contentRect.width, height: entry.contentRect.height}));
  return size
}